import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import Markdown from '@/components/Markdown';
import { chatGenerate, streamChat, performDeepResearch } from '@/services/emailAI';
import { deepResearch as deepResearchService } from '@/services/deepResearch';
import { tracedEmailActions } from '@/services/tracedEmailActions';
// UNIFIED API ARCHITECTURE:
// All email operations flow through: AIChat → gpt5NanoAgent → tracedEmailActions → emailActions → Microsoft Graph API
// This ensures consistent API tracing, monitoring, and error handling across all assistants
import { Textarea } from '@/components/ui/textarea';
import { blink } from '@/lib/blink';
import { AgentModeToggle } from '@/components/AgentModeToggle';
import { orchestratorMiddleware } from '@/services/orchestratorMiddleware';
import type { AgentMode } from '@/types/agentMode';
import {
  Send,
  Zap,
  Paperclip,
  Mic,
  StopCircle,
  Bot,
  User,
  Copy,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Sparkles,
  Mail,
  Search,
  Calendar,
  FileText,
  Settings,
  Loader2,
} from 'lucide-react';

interface Message {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actions?: string[];
  attachments?: string[];
}

const suggestedPrompts = [
  {
    icon: Mail,
    title: 'Show recent emails',
    prompt: 'What are my recent emails today?',
  },
  {
    icon: Send,
    title: 'Send an email',
    prompt: 'Send an email to jp@naible.ai saying hello and asking how the project is going',
  },
  {
    icon: Search,
    title: 'Find urgent emails',
    prompt: 'Find all urgent emails from this week and summarize them',
  },
  {
    icon: Calendar,
    title: 'Schedule meeting',
    prompt: 'Create a meeting tomorrow at 2pm with john@company.com about Q4 planning',
  },
];

// Smoke test runner
async function runUrgentEmailSmokeTest() {
  try {
    const { quickUrgentEmailTest } = await import('@/tests/urgentEmailSmokeTest');
    const result = await quickUrgentEmailTest();
    return result;
  } catch (error) {
    console.error('Failed to run smoke test:', error);
    throw error;
  }
}

export default function AIChat() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      role: 'assistant',
      content: `🎯 **AI Task Assistant Ready - UNIFIED SYSTEM v2.0**

🔥 **NEW: All AI assistants now use the SAME unified API architecture!**

**System Architecture:**
✅ **GPT-5 Nano Agent** → Structured, reliable AI operations
✅ **Traced Email Actions** → Full API tracing and monitoring
✅ **Direct Graph API** → Real Microsoft Graph API calls (no bypassing!)
✅ **Inbox-Search-First** → Always checks your emails before responding

**What This Means:**
• **Consistent behavior** - All assistants work the same way
• **Always searches inbox first** - Even for questions like "who is X?"
• **Real API calls** - Direct Microsoft Graph API integration
• **Full tracing** - Every operation is logged and monitored

**I can do:**
✅ **Execute Actions:** Send emails, create events, manage tasks (via YOUR Gmail/Outlook account)
✅ **Search & Analyze:** Find emails with context from YOUR inbox
✅ **Answer Questions:** Using YOUR actual email data first, then external knowledge
✅ **Automate Workflows:** Sequential operations, smart routing, orchestration

**Just tell me what to do:**
• "Send an email to john@example.com saying hello"
• "Who is Sarah Williams?" ← Will search YOUR emails first!
• "Find urgent emails from this week"
• "Create a meeting tomorrow at 2pm with sarah@company.com"

**🔥 The unified system is active - watch for the "UNIFIED AGENTIC SYSTEM ACTIVE" message!** 🚀`,
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [providerBadge, setProviderBadge] = useState('GPT-5 + Web Search');
  const [useDeepResearch, setUseDeepResearch] = useState(false);
  const [researchProgress, setResearchProgress] = useState<string>('');
  const [user, setUser] = useState<any>(null);
  const [agentMode, setAgentMode] = useState<AgentMode>('action');
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Lazy load AI provider settings
    setTimeout(() => {
      const p = localStorage.getItem('ai_provider') || 'openai';
      const m = localStorage.getItem('ai_model') || (p === 'openai' ? 'gpt-5' : p === 'anthropic' ? 'claude-4.5' : 'gpt-5');
      const label = p === 'openai' ? `OpenAI ${m} + Web Search` : (p === 'anthropic' ? `Anthropic • ${m}` : 'Blink AI');
      setProviderBadge(label);
    }, 0);

    // Load user for AI context and initialize orchestrator
    const unsubscribe = blink.auth.onAuthStateChanged(async (state) => {
      setUser(state.user);
      
      if (state.user) {
        // Initialize orchestrator with user context
        await orchestratorMiddleware.initialize(state.user.id);
        
        // Get current agent mode from orchestrator
        const currentMode = orchestratorMiddleware.getAgentMode();
        setAgentMode(currentMode);
      }
    });

    return unsubscribe;
  }, []);

  // Handler for mode changes
  const handleModeChange = async (mode: AgentMode) => {
    setAgentMode(mode);
    await orchestratorMiddleware.setAgentMode(mode);
    
    // Add a system message to inform user
    const modeMessage: Message = {
      id: messages.length + 1,
      role: 'assistant',
      content: mode === 'answer' 
        ? '🔵 **Switched to Answer Mode**\n\nI will now focus on searching and retrieving information. I won\'t send emails or make changes to your calendar or tasks.\n\nCapabilities:\n• Search emails and calendar\n• Analyze contacts and relationships\n• Generate summaries and insights\n• Extract action items\n\n✅ Safe read-only operations only'
        : '🟠 **Switched to Action Mode**\n\nI now have full execution capabilities. I can send emails, create calendar events, and manage your tasks.\n\nAll capabilities enabled:\n• Search and analysis (from Answer mode)\n• Send and reply to emails\n• Create and update calendar events\n• Manage tasks and workflows\n\n⚠️ Actions will make real changes via Microsoft Graph API',
      timestamp: new Date(),
    };
    
    setMessages((prev) => [...prev, modeMessage]);
  };

  useEffect(() => {
    // Scroll to bottom when new messages are added
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const prompt = input;
    const userMessage: Message = {
      id: messages.length + 1,
      role: 'user',
      content: prompt,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    const assistantId = messages.length + 2
    setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', content: '', timestamp: new Date(), actions: ['View Emails', 'Draft Response', 'Archive Newsletters'] }])
    
    // CRITICAL FIX: ALL queries should search inbox first for context
    // Not just explicit email commands - even "who is John Doe" should check emails
    const emailActionKeywords = /send|reply|forward|archive|delete|trash|compose/i;
    const emailSearchKeywords = /search|find|show|list|urgent|filter|look for|get/i;
    const isEmailAction = emailActionKeywords.test(prompt);
    const isEmailSearch = emailSearchKeywords.test(prompt) && /email/i.test(prompt);
    const isEmailCommand = isEmailAction || isEmailSearch;
    
    // ALL user queries should have inbox context (not just email commands)
    // This ensures even "who is X" queries search emails first
    const shouldSearchInbox = true; // ALWAYS search inbox for context
    
    // UNIFIED SYSTEM: ALL queries go through gpt5NanoAgent when user is authenticated
    const shouldUseUnifiedAgent = user?.id && shouldSearchInbox;
    
    // Check if this is a simple "send email to X" command without subject/body
    const simpleSendEmailMatch = prompt.match(/send\s+(?:an?\s+)?email\s+to\s+([\w._%+-]+@[\w.-]+\.\w+)/i);
    const hasSubjectOrBody = /subject|body|saying|about|message/i.test(prompt);
    
    // Handle simple "send email to X" command - help user complete the email
    if (simpleSendEmailMatch && !hasSubjectOrBody && user?.id) {
      const recipientEmail = simpleSendEmailMatch[1];
      
      setMessages(prev => prev.map(m => 
        m.id === assistantId ? { 
          ...m, 
          content: `✉️ **I can help you send an email to ${recipientEmail}!**\n\nI'll need a bit more information to compose the email:\n\n**Option 1: Tell me what to say**\nJust reply with something like:\n• "Send an email to ${recipientEmail} saying hello and asking about their project"\n• "Email ${recipientEmail} with subject 'Meeting Tomorrow' and say we need to reschedule"\n\n**Option 2: I'll help you draft it**\nTell me:\n• What's the subject?\n• What should the message say?\n\nOnce you provide these details, I'll send the email immediately! 🚀`
        } : m
      ));
      setIsTyping(false);
      return;
    }
    
    // CRITICAL: Route ALL queries through unified agent (not just email commands)
    if (shouldUseUnifiedAgent) {
      // Route ALL queries through GPT-5 Nano Agent which searches inbox FIRST
      // UNIFIED: Use tracedEmailActions for consistent API monitoring across all assistants
      console.log('🔥 UNIFIED SYSTEM ACTIVE: Using GPT-5 Nano Agent for query:', prompt);
      console.log('✅ Unified API Flow: gpt5NanoAgent → tracedEmailActions → emailActions → Microsoft Graph API');
      console.log('📧 This query will search inbox FIRST before any response');
      
      tracedEmailActions.setUserId(user.id);
      
      try {
        // Import the executeUserIntent function which handles orchestration AND inbox search
        const { executeUserIntent, gpt5NanoAgent } = await import('@/services/gpt5NanoAgent');
        
        console.log('🔍 Unified system will search inbox FIRST before responding');
        console.log('📧 All API calls will be traced and monitored');
        
        // Check if this is a multi-action command that should use orchestration
        const isMultiAction = prompt.match(/and|then|after|also|,/gi)?.length || 0 > 2;
        
        setMessages(prev => prev.map(m => 
          m.id === assistantId ? { 
            ...m, 
            content: `🔥 **UNIFIED AGENTIC SYSTEM ACTIVE**\n\n✅ **Using unified GPT-5 Nano Agent**\n✅ **Inbox search will run FIRST** (even for "who is X" queries)\n✅ **All API calls traced**: gpt5NanoAgent → tracedEmailActions → emailActions → Graph API\n✅ **Same system as AI Task Assistant** - consistent agentic logic\n${isMultiAction ? '\n🎭 **Orchestration Mode Enabled** - Actions will be executed intelligently with human-like timing.\n' : ''}\n\n⏳ **Searching your inbox for context...**\n💡 _This ensures I give you answers based on YOUR actual email data, not generic knowledge._` 
          } : m
        ));
        
        // Execute the user's intent through the structured agent
        const result = await executeUserIntent(prompt);
        
        if (result.success) {
          const executionResult = result.execution_result;
          let responseContent = `✅ **Action Completed Successfully!**\n\n`;
          
          // Format response based on action type
          if (executionResult.action === 'get_recent_emails') {
            responseContent += `**Recent Emails**\n\n`;
            responseContent += `📅 **Timeframe:** ${executionResult.timeframe.replace('_', ' ')}\n`;
            responseContent += `📊 **Results Found:** ${executionResult.resultsCount}\n\n`;
            
            if (executionResult.results && executionResult.results.length > 0) {
              responseContent += `**Your Recent Emails:**\n\n`;
              executionResult.results.slice(0, 10).forEach((email: any, idx: number) => {
                const isUnread = !email.isRead;
                const unreadBadge = isUnread ? ' 🔵 **NEW**' : '';
                const isImportant = email.isImportant;
                const importantBadge = isImportant ? ' 🔴 **IMPORTANT**' : '';
                
                responseContent += `${idx + 1}. **${email.subject || '(No Subject)'}**${unreadBadge}${importantBadge}\n`;
                responseContent += `   From: ${email.senderName || email.senderEmail || 'Unknown'}\n`;
                responseContent += `   Date: ${new Date(email.receivedDate).toLocaleString()}\n`;
                
                if (email.snippet) {
                  responseContent += `   Preview: ${email.snippet.substring(0, 100)}...\n`;
                }
                
                if (email.hasAttachments) {
                  responseContent += `   📎 Has attachments\n`;
                }
                
                responseContent += `\n`;
              });
              
              if (executionResult.resultsCount > 10) {
                responseContent += `\n_Showing 10 of ${executionResult.resultsCount} total emails_\n`;
              }
            } else {
              responseContent += `No emails found for the specified timeframe.\n\n`;
              responseContent += `💡 **Tip:** If you recently connected your email account, it may still be syncing. Try again in a few moments or check a different timeframe.\n`;
            }
          } else if (executionResult.action === 'email_send') {
            responseContent += `**Email Sent**\n\n`;
            responseContent += `📧 **To:** ${executionResult.recipients.join(', ')}\n`;
            responseContent += `📨 **Message ID:** ${executionResult.messageId}\n`;
            responseContent += `⏰ **Timestamp:** ${new Date(executionResult.timestamp).toLocaleString()}\n\n`;
            responseContent += `The email has been sent successfully via Microsoft Graph API using your connected account.\n\n`;
            responseContent += `**Agent Analysis:**\n${result.agent_response.thinking || 'Processed email send request'}\n\n`;
            responseContent += `**Reasoning:**\n${result.agent_response.reasoning}\n`;
          } else if (executionResult.action === 'email_draft') {
            responseContent += `**Email Draft Created**\n\n`;
            responseContent += `📧 **To:** ${executionResult.recipients?.join(', ') || 'No recipients'}\n`;
            responseContent += `📝 **Subject:** ${executionResult.subject || '(No Subject)'}\n`;
            responseContent += `⏰ **Created:** ${new Date(executionResult.timestamp).toLocaleString()}\n\n`;
            responseContent += `The draft has been created successfully. You can view and edit it in your email drafts folder.\n\n`;
            if (result.agent_response.thinking) {
              responseContent += `**Agent Analysis:**\n${result.agent_response.thinking}\n\n`;
            }
            if (result.agent_response.reasoning) {
              responseContent += `**Reasoning:**\n${result.agent_response.reasoning}\n`;
            }
          } else if (executionResult.action === 'email_search') {
            responseContent += `**Email Search Results**\n\n`;
            responseContent += `🔍 **Query:** "${executionResult.query}"\n`;
            responseContent += `📊 **Results Found:** ${executionResult.resultsCount}\n\n`;
            if (executionResult.results && executionResult.results.length > 0) {
              responseContent += `**Top Results:**\n`;
              executionResult.results.slice(0, 5).forEach((email: any, idx: number) => {
                const isUrgent = email.isImportant || email.is_important;
                const urgentBadge = isUrgent ? ' 🔴 **URGENT**' : '';
                responseContent += `\n${idx + 1}. **${email.subject || '(No Subject)'}**${urgentBadge}\n`;
                responseContent += `   From: ${email.senderName || email.sender_name || email.senderEmail || email.sender_email || 'Unknown'}\n`;
                responseContent += `   Date: ${new Date(email.receivedDate || email.received_date).toLocaleString()}\n`;
                if (email.snippet) {
                  responseContent += `   ${email.snippet.substring(0, 100)}...\n`;
                }
              });
              
              // Add AI summary if it's an urgent email search
              if (executionResult.query && executionResult.query.toLowerCase().includes('urgent')) {
                responseContent += `\n\n**AI Summary of Urgent Emails:**\n\n`;
                responseContent += `_Generating summary..._\n`;
                
                // Generate AI summary asynchronously
                setTimeout(async () => {
                  try {
                    const emailList = executionResult.results.slice(0, 10).map((e: any, i: number) => 
                      `${i + 1}. From: ${e.senderEmail}, Subject: "${e.subject}", Date: ${new Date(e.receivedDate).toLocaleDateString()}, Snippet: ${e.snippet || 'N/A'}`
                    ).join('\n');
                    
                    const { text } = await blink.ai.generateText({
                      prompt: `Summarize these urgent emails in 3-5 bullet points, highlighting key action items and deadlines:\n\n${emailList}`,
                      maxTokens: 500
                    });
                    
                    setMessages(prev => prev.map(m => 
                      m.id === assistantId ? {
                        ...m,
                        content: m.content.replace('_Generating summary..._', text.trim())
                      } : m
                    ));
                  } catch (error) {
                    console.error('Failed to generate summary:', error);
                    setMessages(prev => prev.map(m => 
                      m.id === assistantId ? {
                        ...m,
                        content: m.content.replace('_Generating summary..._', '_Unable to generate summary at this time._')
                      } : m
                    ));
                  }
                }, 100);
              }
            } else {
              responseContent += `No emails found matching your search criteria.\n\n`;
              if (executionResult.query && executionResult.query.toLowerCase().includes('urgent')) {
                responseContent += `💡 **Tip:** If you recently connected your email account, try syncing your emails first or check if urgent emails are marked as "important" in your inbox.\n`;
              }
            }
          } else if (executionResult.action === 'calendar_create') {
            responseContent += `**Calendar Event Created**\n\n`;
            responseContent += `📅 **Event:** ${executionResult.title}\n`;
            responseContent += `🕐 **Start:** ${new Date(executionResult.start).toLocaleString()}\n`;
            responseContent += `🆔 **Event ID:** ${executionResult.eventId}\n`;
          } else if (executionResult.action === 'answer_question' || executionResult.action === 'provide_information' || executionResult.action === 'information_retrieval') {
            // General question answered with inbox context
            responseContent = `✅ **Answer Based on Your Emails:**\n\n`;
            
            // Show the synthesized answer from the agent
            if (executionResult.answer || executionResult.information) {
              responseContent += `${executionResult.answer || executionResult.information}\n\n`;
            } else {
              // Fallback to reasoning if no answer in execution result
              responseContent += `${result.agent_response?.reasoning || 'Information retrieved from your emails.'}\n\n`;
            }
            
            // Optionally show inbox search results as supporting evidence (collapsed by default)
            if (result.agent_response?.thinking && result.agent_response.thinking.includes('INBOX SEARCH RESULTS')) {
              // Extract and format the inbox search section
              const thinkingText = result.agent_response.thinking;
              const searchResultsStart = thinkingText.indexOf('🔍 **INBOX SEARCH RESULTS');
              if (searchResultsStart !== -1) {
                const searchResultsEnd = thinkingText.indexOf('**CRITICAL:**', searchResultsStart);
                const searchResultsSection = searchResultsEnd !== -1 
                  ? thinkingText.substring(searchResultsStart, searchResultsEnd)
                  : thinkingText.substring(searchResultsStart);
                
                responseContent += `\n\n---\n\n**📧 Email Evidence (${searchResultsSection.split('**Email ').length - 1} emails found):**\n\n`;
                responseContent += `<details><summary>Click to see email details</summary>\n\n${searchResultsSection}\n\n</details>\n\n`;
              }
            }
            
            // If no emails were found, explain that clearly
            if (result.agent_response?.thinking && result.agent_response.thinking.includes('No relevant emails found')) {
              responseContent = `💡 **No Email Context Found**\n\nI couldn't find any emails in your inbox related to this query. `;
              
              // Still show the answer if the agent provided one
              if (executionResult.answer || executionResult.information) {
                responseContent += `However, based on general knowledge:\n\n${executionResult.answer || executionResult.information}`;
              } else {
                responseContent += `Please try a different search or connect your email account to search your actual emails.`;
              }
            }
          } else {
            responseContent += JSON.stringify(executionResult, null, 2);
          }
          
          setMessages(prev => prev.map(m => 
            m.id === assistantId ? { ...m, content: responseContent } : m
          ));
        } else {
          setMessages(prev => prev.map(m => 
            m.id === assistantId ? { 
              ...m, 
              content: `❌ **Error processing email action**\n\n${result.error}\n\n**Agent Response:**\n${result.agent_response ? result.agent_response.thinking : 'No additional details'}` 
            } : m
          ));
        }
      } catch (error: any) {
        setMessages(prev => prev.map(m => 
          m.id === assistantId ? { 
            ...m, 
            content: `❌ **Error processing email action**\n\n${error.message}` 
          } : m
        ));
      }
      
      setIsTyping(false);
      return;
    }
    
    // Email actions (send/reply/forward) REQUIRE authentication - no fallback to Blink SDK
    // All email operations MUST go through Microsoft Graph API via connected accounts
    if (!user && isEmailAction) {
      setMessages(prev => prev.map(m => 
        m.id === assistantId ? { 
          ...m, 
          content: `🔐 **Authentication Required**\n\nTo send emails, please sign in first and connect your Gmail or Microsoft Outlook account.\n\n**Why?** Email sending uses your real email account via Microsoft Graph API or Gmail API - not a third-party service. This ensures:\n• Emails come from YOUR address\n• Full control over your account\n• Compliance with email security standards\n\n[Sign in to get started](/login)` 
        } : m
      ));
      setIsTyping(false);
      return;
    }
    
    // Email search can work with or without OAuth connection (searches local DB)
    if (!user && isEmailSearch) {
      setMessages(prev => prev.map(m => 
        m.id === assistantId ? { 
          ...m, 
          content: `🔐 **Sign In Required**\n\nPlease sign in to search your emails.\n\n[Sign in to continue](/login)` 
        } : m
      ));
      setIsTyping(false);
      return;
    }
    
    // Detect if this is a research-heavy query
    const lowerPrompt = prompt.toLowerCase();
    const isResearchQuery = useDeepResearch || 
                            lowerPrompt.includes('research') ||
                            lowerPrompt.includes('compare') ||
                            lowerPrompt.includes('analyze in depth');
    const maxTokens = isResearchQuery ? 3000 : 800;
    
    // Use Deep Research for comprehensive multi-step queries
    if (useDeepResearch) {
      setResearchProgress('🔬 Starting deep research...');
      
      deepResearchService.streamResearch(
        {
          query: prompt,
          useFullModel: true,
          outputFormat: 'markdown',
          requireCitations: true
        },
        {
          onProgress: (stage, message) => {
            setResearchProgress(message);
            if (stage === 'searching') {
              setMessages(prev => prev.map(m => 
                m.id === assistantId ? { ...m, content: '🔍 **Deep Research in Progress**\n\nGathering information from multiple authoritative sources...\n\n' } : m
              ));
            } else if (stage === 'synthesizing') {
              setMessages(prev => prev.map(m => 
                m.id === assistantId ? { ...m, content: (m.content || '') + '\n\n✨ Synthesizing research findings...\n\n' } : m
              ));
            }
          },
          onChunk: (chunk) => {
            setMessages(prev => prev.map(m => 
              m.id === assistantId ? { ...m, content: (m.content || '') + chunk } : m
            ));
            endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
          },
          onCitation: (citation) => {
            console.log('Citation found:', citation);
          },
          onComplete: (result) => {
            setIsTyping(false);
            setUseDeepResearch(false);
            setResearchProgress('');
            
            // Add metadata footer
            const metadata = `\n\n---\n\n*Research completed in ${(result.metadata.completion_time / 1000).toFixed(1)}s • ${result.citations.length} sources • Quality score: ${result.metadata.quality_score}/100*`;
            setMessages(prev => prev.map(m => 
              m.id === assistantId ? { ...m, content: (m.content || '') + metadata } : m
            ));
          }
        }
      ).catch(err => {
        console.error('Deep research error:', err);
        setIsTyping(false);
        setUseDeepResearch(false);
        setResearchProgress('');
        setMessages(prev => prev.map(m => 
          m.id === assistantId ? { ...m, content: `Error during deep research: ${err.message}\n\nFalling back to standard response...` } : m
        ));
      });
    } else {
      // Check if this is an email or calendar command that should use chatGenerate instead
      const isEmailOrCalendarCommand = 
        lowerPrompt.includes('send email') || 
        lowerPrompt.includes('reply') || 
        lowerPrompt.includes('forward') ||
        lowerPrompt.includes('archive') ||
        lowerPrompt.includes('delete') ||
        lowerPrompt.includes('search emails') ||
        lowerPrompt.includes('calendar') ||
        lowerPrompt.includes('meeting');

      if (isEmailOrCalendarCommand && user?.id) {
        // Use chatGenerate for command execution
        chatGenerate(prompt, maxTokens, {
          useWebSearch: true,
          useOrchestration: isResearchQuery,
          userId: user.id
        }).then(response => {
          setMessages(prev => prev.map(m => 
            m.id === assistantId ? { ...m, content: response } : m
          ));
          setIsTyping(false);
        }).catch(err => {
          console.error('Command execution error:', err);
          setMessages(prev => prev.map(m => 
            m.id === assistantId ? { ...m, content: `Error: ${err.message}` } : m
          ));
          setIsTyping(false);
        });
      } else {
        // Standard streaming with optional orchestration
        streamChat({
          prompt,
          maxTokens: isResearchQuery ? 3000 : 800,
          useWebSearch: true, // Always enable web search for up-to-date info
          useOrchestration: isResearchQuery,
          onChunk: (chunk) => {
            setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: (m.content || '') + chunk } : m))
            endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
          },
          onDone: (final) => {
            setIsTyping(false)
            if (!final) {
              setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: 'I generated a response, but it was empty. Please try again with more detail.' } : m))
            }
          }
        })
      }
    }
  };

  const handlePromptClick = (prompt: string) => {
    setInput(prompt);
  };

  const toggleRecording = () => {
    setIsRecording(!isRecording);
    if (!isRecording) {
      // Start recording
      setTimeout(() => {
        setIsRecording(false);
        setInput('Find all emails from John Doe about the Q4 report');
      }, 3000);
    }
  };

  return (
    <div className="h-full flex">
      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border/50 bg-card/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  AI Task Assistant 
                  <Badge variant="default" className="text-xs bg-accent">
                    UNIFIED v2.0
                  </Badge>
                </h2>
                <p className="text-sm text-muted-foreground">Task Executor • GPT-5 + Web Search + Unified API</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <AgentModeToggle
                currentMode={agentMode}
                onModeChange={handleModeChange}
                disabled={isTyping}
              />
              <button
                onClick={() => setUseDeepResearch(!useDeepResearch)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  useDeepResearch ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                }`}
              >
                {useDeepResearch ? '🔬 Deep Research ON' : '🔬 Deep Research'}
              </button>
              <Badge variant="outline" className="text-xs">
                <Sparkles className="w-3 h-3 mr-1" />
                {providerBadge}
              </Badge>
              <Button variant="ghost" size="icon">
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon">
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
          <div className="space-y-4 max-w-3xl mx-auto">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-primary-foreground" />
                  </div>
                )}
                <div
                  className={`max-w-[70%] ${
                    message.role === 'user' ? 'order-first' : ''
                  }`}
                >
                  <div
                    className={`p-4 rounded-lg ${
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card border border-border'
                    }`}
                  >
                    {message.role === 'assistant' ? (
                      <Markdown content={message.content} className="prose prose-zinc dark:prose-invert max-w-none text-sm" />
                    ) : (
                      <div className="whitespace-pre-wrap text-sm">{message.content}</div>
                    )}
                    {message.actions && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {message.actions.map((action) => (
                          <Button
                            key={action}
                            variant={message.role === 'user' ? 'secondary' : 'outline'}
                            size="sm"
                          >
                            {action}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-muted-foreground">
                      {message.timestamp.toLocaleTimeString()}
                    </span>
                    {message.role === 'assistant' && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-6 w-6">
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6">
                          <ThumbsUp className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6">
                          <ThumbsDown className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                {message.role === 'user' && (
                  <div className="w-8 h-8 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4" />
                  </div>
                )}
              </div>
            ))}
            <div ref={endRef} />
            {isTyping && (
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Bot className="w-4 h-4 text-primary-foreground" />
                </div>
                <div className="bg-card border border-border p-4 rounded-lg">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" />
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce delay-100" />
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce delay-200" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="p-4 border-t border-border/50 bg-card/50">
          {/* Suggested Prompts */}
          {messages.length === 1 && (
            <div className="grid grid-cols-2 gap-2 mb-4">
              {suggestedPrompts.map((prompt, index) => (
                <button
                  key={index}
                  onClick={() => handlePromptClick(prompt.prompt)}
                  className="flex items-center gap-3 p-3 text-left rounded-lg border border-border hover:bg-accent/5 transition-colors"
                >
                  <prompt.icon className="h-4 w-4 text-primary" />
                  <span className="text-sm">{prompt.title}</span>
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <Button variant="outline" size="icon">
              <Paperclip className="h-4 w-4" />
            </Button>
            <Input
              placeholder="Tell me what to do - I execute tasks!"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              className="flex-1"
            />
            <Button
              variant={isRecording ? 'destructive' : 'outline'}
              size="icon"
              onClick={toggleRecording}
            >
              {isRecording ? (
                <StopCircle className="h-4 w-4" />
              ) : (
                <Mic className="h-4 w-4" />
              )}
            </Button>
            <Button onClick={sendMessage} disabled={!input.trim()}>
              {isTyping ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send
            </Button>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div className="w-80 border-l border-border/50 bg-card/50 p-4 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Conversation Context</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Messages</span>
                <span>{messages.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Emails Analyzed</span>
                <span>247</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Actions Taken</span>
                <span>12</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Recent Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="truncate">Drafted reply to John Doe</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Search className="h-4 w-4 text-muted-foreground" />
                <span className="truncate">Found 5 urgent emails</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="truncate">Added 2 meetings to calendar</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">AI Capabilities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Badge variant="secondary" className="w-full justify-start">
                <Zap className="mr-2 h-3 w-3" />
                Email Search & Analysis
              </Badge>
              <Badge variant="secondary" className="w-full justify-start">
                <FileText className="mr-2 h-3 w-3" />
                Draft Generation
              </Badge>
              <Badge variant="secondary" className="w-full justify-start">
                <Sparkles className="mr-2 h-3 w-3" />
                Smart Classification
              </Badge>
              <Badge variant="secondary" className="w-full justify-start">
                <Calendar className="mr-2 h-3 w-3" />
                Task Extraction
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}